import type { ProviderConfig } from "../settings/settingsStore.js";
import { providerBody } from "../epg/api.js";
import { useMirrorStore } from "./mirrorStore.js";

/**
 * Resolve the tokenized fallback streams for a channel's mirrors (Section 16).
 *
 * The subtlety that makes cross-provider mirrors different from a provider's
 * own `altStreamUrls`: each fallback lives on a DIFFERENT user-owned provider,
 * so it must be stream-tokenized with THAT provider's credentials, not the
 * active one's. The stream-token POST (via {@link resolveStreamUrl}) builds its
 * request body from `providerBody()` — the active provider. That is exactly the
 * leak class that caused the connection surge: a raw URL sent un-tokenized hits
 * the provider directly (no gate, invisible in logs). A mirror fallback sent
 * with the wrong provider's credentials would be just as broken — the redacted
 * URL would never resolve to a playable stream on the target account.
 *
 * So the split is:
 * - {@link buildMirrorFallbacks} is pure: it matches mirrors to a channel,
 *   resolves each fallback to its owning provider's CURRENT config (mirrors
 *   never hold credentials; settings stay authoritative), and builds per-
 *   provider request bodies. Fully unit-testable, no DOM.
 * - {@link tokenizeMirrorFallbacks} is the browser-only half: it passes each
 *   fallback's raw URL + its OWN provider body to the stream-token endpoint so
 *   the token is minted against the right account.
 */

/** A fallback ready to tokenize: its raw provider URL plus the creditials-body
 *  that must accompany the token request. */
export interface MirrorFallbackCandidate {
  providerId: string;
  channelName: string;
  rawUrl: string;
  /** The `providerBody(...)` payload for THIS fallback's provider. */
  creds: Record<string, unknown>;
}

/**
 * Pure: given a channel, its mirrors, and all configured providers, produce the
 * fallbacks whose owning provider is still configured. Mirrors whose provider
 * was removed, renamed id, or lost its credentials are dropped — a dead link
 * is worse than no link (it costs a token + a connection on the wrong account).
 */
export function buildMirrorFallbacks(
  sourceId: string,
  channelId: string,
  providers: ProviderConfig[],
): MirrorFallbackCandidate[] {
  const mirror = useMirrorStore.getState().getMirror(sourceId, channelId);
  if (!mirror) return [];
  const byId = new Map(providers.map((p) => [p.id, p]));
  const out: MirrorFallbackCandidate[] = [];
  for (const fallback of mirror.fallbacks) {
    const provider = byId.get(fallback.providerId);
    if (!provider) continue; // provider no longer configured
    // Only offer a fallback whose provider is usable (validated or has creds).
    if (provider.isValid === false && !provider.xtreamBaseUrl && !provider.m3uUrl) continue;
    // Use the provider-native URL the user linked at mirror time. It may carry
    // a redacted placeholder; tokenizeMirrorFallbacks swaps in the target
    // provider's CURRENT credentials, same as the primary-URL flow. A missing
    // URL is a dead link and is never offered.
    if (!fallback.streamUrl) continue;
    out.push({ providerId: fallback.providerId, channelName: fallback.channelName, rawUrl: fallback.streamUrl, creds: providerBody(provider) });
  }
  return out;
}

/** Browser-only: tokenize every candidate against ITS OWN provider and return
 *  the successfully-minted same-origin URLs, dropping failures (a failing
 *  fallback must never be fatal to the primary channel). */
export async function tokenizeMirrorFallbacks(candidates: MirrorFallbackCandidate[]): Promise<string[]> {
  const { resolveStreamUrlWithProvider } = await import("../player/streamToken.js");
  const results = await Promise.all(
    candidates.map(async (c) => {
      try {
        const resolved = await resolveStreamUrlWithProvider(c.rawUrl, c.creds);
        return resolved.url;
      } catch {
        return null;
      }
    }),
  );  return results.filter((url): url is string => url !== null);
}

/**
 * Build the mirrorKey (`$sourceId:$channelId`) for a live channel so call
 * sites can hand it to playSource, which resolves the channel's mirrors.
 */
export function mirrorKeyFor(sourceId: string, channelId: string): string {
  return `${sourceId}:${channelId}`;
}
